/**
 * `/join` — the member's door into an existing team (#1720).
 *
 * **Honest about the mechanism.** Tarii has no join-by-code, no
 * email-domain auto-join and no team directory: a membership is minted
 * by accepting an invite token the lead issued
 * (`POST /api/invites/{token}/accept`). This page says that plainly
 * rather than offering a search box that would find nothing. Its job is
 * to convert the two states a person actually arrives in:
 *
 *   1. *They have the link* (in an email, in a chat message, on a phone
 *      they're reading from) → paste it here and we hand them to
 *      `/invite/:token`, which is the existing preview/accept flow. No
 *      new backend, no second acceptance path to keep in sync.
 *   2. *They have nothing yet* → they need their lead to invite them,
 *      to the address on this account. So we name the exact menu path
 *      (People → Invite by email) and show them their own email to forward.
 *
 * **Paste tolerance** lives in `lib/inviteTokenInput.ts` — a whole URL,
 * a URL with the `/accept` suffix or a query string, or the bare token
 * all resolve. Anything else gets a calm inline correction, never a
 * navigation to a page that will 404 at them.
 *
 * **Where it sits:** inside the auth gate, outside the AppShell — same
 * placement as `/onboarding` and `/start`. Being authenticated is what
 * makes the "your lead invites *this* address" line possible, and the
 * accept flow needs a session anyway.
 *
 * **Focal hierarchy** (`feedback_ui_section_focal_component.md`): the
 * paste field is the focal component — it is the only thing on the page
 * that completes the journey. The "no invite yet" guidance sits below
 * it as supporting context, and the two escapes (set up a team instead,
 * back to the chooser) are quiet text actions at the foot.
 *
 * **Register** (`feedback_avoid_ai_generic_ui.md`): the landing page's
 * editorial system — mono-uppercase micro-labels, hairline rules, no
 * card grid, no emoji.
 */
import { ArrowRight } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Logo } from "../components/Logo";
import { Button, Input, Label } from "../components/ui";
import { useAuth } from "../lib/AuthContext";
import { parseInviteTokenInput } from "../lib/inviteTokenInput";

const PARSE_ERROR =
  "That doesn't look like a Tarii invite link — paste the whole link, or just the code at the end.";

export function JoinPage() {
  const { account } = useAuth();
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const token = parseInviteTokenInput(value);
    if (!token) {
      setError(PARSE_ERROR);
      return;
    }
    setError(null);
    // `/invite/:token` is the unauthenticated PREVIEW surface, but it
    // short-circuits an already-signed-in visitor straight to
    // `/invite/:token/accept`. Sending people to the preview URL keeps
    // ONE entry point for every invite arrival — email link, pasted
    // link, or this form.
    navigate(`/invite/${encodeURIComponent(token)}`);
  }

  return (
    <div className="min-h-screen bg-bg">
      <div className="mx-auto w-full max-w-3xl px-5 sm:px-8">
        <header className="flex items-center justify-between gap-4 border-border border-b py-5">
          <Link to="/" aria-label="Tarii home">
            <Logo className="h-7" />
          </Link>
          <span className="font-mono text-[10px] text-fg-subtle uppercase tracking-[0.18em]">
            Join a team
          </span>
        </header>

        {/* No border-t on the first rule: the header's own hairline is
            three lines up, and two rules with nothing between them
            read as a mistake rather than a system. */}
        <main className="pt-6 pb-12 sm:pt-8 sm:pb-16">
          <div className="flex items-baseline justify-between gap-4">
            <span className="font-mono text-[10px] text-accent-fg tracking-[0.18em]">01</span>
            <span className="font-mono text-[10px] text-fg-subtle uppercase tracking-[0.18em]">
              How joining works
            </span>
          </div>

          <h1 className="mt-4 max-w-[20ch] font-display text-[2rem] text-fg leading-[1.08] tracking-[-0.03em] sm:text-[2.5rem] sm:leading-[1.04]">
            Joining runs on an invite.
          </h1>
          <p className="mt-3 max-w-[58ch] text-base text-fg-muted leading-relaxed">
            Your lead sends you a link, and that link puts you on the team.
          </p>

          {/* Focal: the paste field. */}
          <form
            onSubmit={onSubmit}
            className="mt-9 border-border border-l-2 border-l-accent border-y py-6 pl-4 sm:pl-5"
            data-testid="join-invite-form"
          >
            <Label htmlFor="join-invite-input" className="font-display text-base text-fg">
              Paste your invite link
            </Label>
            <p className="mt-1 max-w-[56ch] text-fg-muted text-sm leading-relaxed">
              The whole link works, or just the code at the end.
            </p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start">
              <Input
                id="join-invite-input"
                name="invite"
                value={value}
                onChange={(event) => {
                  setValue(event.target.value);
                  if (error) setError(null);
                }}
                placeholder="https://tarii-time.com/invite/…"
                autoComplete="off"
                spellCheck={false}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "join-invite-error" : undefined}
                className="h-10 sm:max-w-[28rem]"
              />
              <Button type="submit" size="lg" className="self-start">
                Continue
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            {error ? (
              <p
                id="join-invite-error"
                role="alert"
                className="mt-3 max-w-[56ch] text-danger text-sm leading-relaxed"
              >
                {error}
              </p>
            ) : null}
          </form>

          {/* Supporting: what to do when there is no link yet. */}
          <section aria-labelledby="join-no-invite" className="mt-10">
            <div className="flex items-baseline justify-between gap-4 border-border border-t pt-3">
              <span className="font-mono text-[10px] text-accent-fg tracking-[0.18em]">02</span>
              <span className="font-mono text-[10px] text-fg-subtle uppercase tracking-[0.18em]">
                No link yet
              </span>
            </div>
            <h2
              id="join-no-invite"
              className="mt-5 font-display text-fg text-xl tracking-[-0.02em] sm:text-2xl"
            >
              Ask your lead to invite you.
            </h2>
            <p className="mt-3 max-w-[58ch] text-fg-muted text-sm leading-relaxed">
              They open <span className="text-fg">People</span> →{" "}
              <span className="text-fg">Invite by email</span> and enter your email address.
            </p>
            {account?.email ? (
              <div className="mt-5 border-border border-t pt-4" data-testid="join-account-email">
                <span className="font-mono text-[10px] text-fg-subtle uppercase tracking-[0.18em]">
                  Give them this address
                </span>
                <p className="mt-2 font-mono text-fg text-sm">{account.email}</p>
                <p className="mt-1.5 max-w-[54ch] text-fg-subtle text-xs leading-relaxed">
                  Invites are emailed — this is the address to give them.
                </p>
              </div>
            ) : null}
          </section>

          <nav
            aria-label="Other ways to start"
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 border-border border-t pt-5 text-sm"
          >
            <Link to="/onboarding" className="text-accent-fg underline-offset-4 hover:underline">
              Set up a team instead
            </Link>
            <Link to="/start" className="text-fg-subtle underline-offset-4 hover:text-fg-muted">
              Back to the start
            </Link>
          </nav>
        </main>
      </div>
    </div>
  );
}
